import { useFormContext, useWatch } from 'react-hook-form';
import { If, Section, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useEffect, useMemo } from 'react';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { ItemDetailsFormData } from './types';
import { useItemDetailsForm } from './useItemDetailForm';

export function ShoeDetailsSection() {
  const { control, setValue } = useFormContext<ItemDetailsFormData>();
  const { isFieldDisabled, isFieldRequired, makeOptionsFromMetaData } =
    useItemDetailsForm();

  const currentDivision = useWatch({ control, name: 'division' });

  const isWomenShoes = useMemo(
    () => !!currentDivision?.toLowerCase().includes('women'),
    [currentDivision],
  );

  const [currentHeelHeight, currentHeelType, currentShoeStyle] = useWatch({
    control,
    name: ['heelHeight', 'heelType', 'shoeStyle'],
  });

  const heelHeightOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_heelHeight', 'Heel Height'),
    [makeOptionsFromMetaData],
  );

  const heelTypeOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_heelType', 'Heel Type'),
    [makeOptionsFromMetaData],
  );

  const shoeStyleOptions = useMemo(
    () => makeOptionsFromMetaData('ns_master_shoeStyle', 'Shoe Style'),
    [makeOptionsFromMetaData],
  );

  useEffect(() => {
    if (
      currentHeelHeight &&
      heelHeightOptions.length > 0 &&
      !heelHeightOptions.some((opt) => opt.value === currentHeelHeight)
    ) {
      setValue('heelHeight', '');
    }
  }, [heelHeightOptions]);

  useEffect(() => {
    if (
      currentHeelType &&
      heelTypeOptions.length > 0 &&
      !heelTypeOptions.some((opt) => opt.value === currentHeelType)
    ) {
      setValue('heelType', '');
    }
  }, [heelTypeOptions]);

  useEffect(() => {
    if (
      currentShoeStyle &&
      shoeStyleOptions.length > 0 &&
      !shoeStyleOptions.some((opt) => opt.value === currentShoeStyle)
    ) {
      setValue('shoeStyle', '');
    }
  }, [shoeStyleOptions]);

  return (
    <Section
      icon="pencil-edit/pencil-eraser-edit-line"
      title="Shoe Details"
      color="pink"
      className="w-full flex flex-col"
    >
      <Stack direction="col" gap="md">
        <Stack direction="row" gap="md">
          <If condition={isWomenShoes}>
            <ControlledFormInput
              name="heelHeight"
              label="Heel Height"
              control={control}
              inputType="multiselect"
              disabled={
                isFieldDisabled('heel_height', false) ||
                heelHeightOptions.length <= 0
              }
              required={isFieldRequired('heel_height', false)}
              inputProps={{
                searchable: true,
                singleSelectWhenSearchable: true,
                className: 'w-full',
                placeholder: 'Select..',
                options: heelHeightOptions,
              }}
            />
            <ControlledFormInput
              name="heelType"
              label="Heel Type"
              control={control}
              inputType="multiselect"
              disabled={
                isFieldDisabled('heel_type', false) ||
                heelTypeOptions.length <= 0
              }
              required={isFieldRequired('heel_type', false)}
              inputProps={{
                searchable: true,
                singleSelectWhenSearchable: true,
                className: 'w-full',
                placeholder: 'Select..',
                options: heelTypeOptions,
              }}
            />
          </If>
          <ControlledFormInput
            name="shoeStyle"
            label="Shoe Style"
            control={control}
            inputType="multiselect"
            disabled={
              isFieldDisabled('shoe_style', false) ||
              shoeStyleOptions.length <= 0
            }
            required={isFieldRequired('shoe_style', false)}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: shoeStyleOptions,
            }}
          />
        </Stack>
      </Stack>
    </Section>
  );
}
